import FileSaver from 'file-saver';
import { sendRequest } from 'spark-state/common/request/reducer';
import { toQueryParamString } from './apiUtil';
import getMessage from 'lodash/get';
import { successMessage, alertMessage } from 'spark-state/common/messages/messagesReducer';
import { getArrayBuffer } from 'spark-util/fileUtils';
import { setBulkDownloadStatus } from 'spark-state/common/bulkdownload/bulkDownloadReducer';
import { dateRange } from 'spark-util/commonUtils';
/**
 * Base implementation for HTTP requests.
 * Each additional service should use the functions defined here
 * so that we can control how all network requests look from here.
 * For instance, for reading/writing enterprises, we would create an
 * EnterpriseService, which would implement these methods with it's
 * own particular interface.
 *
 * Note that each of these methods return a redux action that must be dispatched.
 */

export const get = (url, { queryParameters = {}, responseType, ...options } = {}) =>
	sendRequest({
		...options,
		method: 'get',
		responseType: responseType != null ? responseType : 'json',
		url: `${url}${toQueryParamString(queryParameters)}`
	});

	
export const getCollection = (url, { defaults = {}, params = {} } = {}) => (collectionParams = {}) => {
	const {
		page = defaults.page || 0,
		size = defaults.size || 10,
		searchParam = defaults.searchParam || '',
		sort = defaults.sort || '',
		...query
	} = collectionParams;
	return get(url, {
		queryParameters: {
			page,
			size,
			searchParam,
			sort,
			...params,
			...query
		}
	});
};

export const remove = (url, options) =>
	sendRequest({
		...options,
		method: 'delete',
		url
	});

export const create = (url, data, options) => async (dispatch) => {
	let response = await dispatch(
			sendRequest({
			...options,
			method: 'post',
			url,
			data
		})
	);
	dispatch(setBulkDownloadStatus(false));

	return response;
}


export const update = (url, data, options) =>
	sendRequest({
		...options,
		method: 'put',
		url,
		data
	});	

export const upload = (url, data, options) =>
	sendRequest({
		...options,
		method: 'post',
		headers: { 'Content-Type': 'multipart/form-data' },
		url,
		data
	});

export const download = (url, data, { type, title, ...options }) => async (dispatch) => {
	let response = await dispatch(
		sendRequest({
			...options,
			method: 'get',
			responseType: 'arraybuffer',
			headers: { 'Content-Type': type },
			url,
			data
		})
	);
	FileSaver.saveAs(new Blob([ response.data ], { type }), title);

	return response;
};

// SAF-18351 - Added a new function to view the file from the response
export const viewDocument = (url, data, options) => async (dispatch) => {
	let response = await dispatch(
		sendRequest({
			...options,
			method: 'post', 
			responseType: 'application/json',
			headers: { 'Content-Type': 'application/json' }, 
			url,
			data // Send the data object directly
		})
	);

	return response;
};

export const postDownload = (url, data, { type, ...options }) => async (dispatch) => {
	let response = await dispatch(
		sendRequest({
			...options,
			method: 'post',
			responseType: 'application/json',
			headers: { 'Content-Type': 'application/json' },
			url,
			data
		})
	);
 		dispatch(setBulkDownloadStatus(false))
		if(response.data.byteArray){
		FileSaver.saveAs(new Blob([getArrayBuffer(response.data.byteArray) ], { type }), `Bulkdownload_${dateRange(options.start)}_to_${dateRange(options.end)}.zip`);
			dispatch(successMessage(getMessage(response, 'data.message')));
		} else{
			dispatch(successMessage(getMessage(response, 'data.message')));
		}
		
	return response;
};
export const postDownloadRecipient = (url, data, { type, ...options }) => async (dispatch) => {
	let response = await dispatch(
		sendRequest({
			...options,
			method: 'post',
			responseType: 'application/json',
			headers: { 'Content-Type': 'application/json' },
			url,
			data
		})
	);
 		dispatch(setBulkDownloadStatus(false))
		if(response.data.byteArray){
		FileSaver.saveAs(new Blob([getArrayBuffer(response.data.byteArray) ], { type }), 'BulkDownload.zip');
			dispatch(successMessage(getMessage(response, 'data.message'),'Recipient'));
		} else {
			dispatch(alertMessage(getMessage(response, 'data.message'), 'Recipient'));
		}
		
	return response;
};

export const postDownloadForGapPacket = (url, data, { type, ...options }) => async (dispatch) => {
	let response = await dispatch(
		sendRequest({
			...options,
			method: 'post',
			responseType: 'application/json',
			headers: { 'Content-Type': 'application/json' },
			url,
			data
		})
	);
 		dispatch(setBulkDownloadStatus(false))
		if(response.data.byteArray){
		FileSaver.saveAs(new Blob([getArrayBuffer(response.data.byteArray) ], { type }), `GapPacket_${dateRange(options.start)}_to_${dateRange(options.end)}.zip`);
			dispatch(successMessage(getMessage(response, 'data.message')));
		} else{
			dispatch(successMessage(getMessage(response, 'data.message')));
		}
	return response;
};

export const postDownloadForGPSet = (url, data, { type, setNumber, gpTitle, ...options }) => async (dispatch) => {
	let response = await dispatch(
		sendRequest({
			...options,
			method: 'post',
			responseType: 'application/json',
			headers: { 'Content-Type': 'application/json' },
			url,
			data
		})
	);
 		dispatch(setBulkDownloadStatus(false))
		if(response.data.byteArray){
		FileSaver.saveAs(new Blob([getArrayBuffer(response.data.byteArray) ], { type }), `${gpTitle}_Set_${setNumber}.zip`);
			dispatch(successMessage(getMessage(response, 'data.message')));
		} else{
			dispatch(successMessage(getMessage(response, 'data.message')));
		}
		
	return response;
};
export const postDownloadForRecipientGPSet = (url, data, { type, setNumber, gpTitle, ...options }) => async (dispatch) => {
	let response = await dispatch(
		sendRequest({
			...options,
			method: 'post',
			responseType: 'application/json',
			headers: { 'Content-Type': 'application/json' },
			url,
			data
		})
	);
 		dispatch(setBulkDownloadStatus(false))
		if(response.data.byteArray){
			FileSaver.saveAs(new Blob([getArrayBuffer(response.data.byteArray) ], { type }), `${gpTitle}_Set_${setNumber}.zip`);
			if(response.data.success){
				dispatch(successMessage(getMessage(response, 'data.message')));
			} else {
				dispatch(successMessage(getMessage(response, 'data.message')));
			}
		} else{
			dispatch(successMessage(getMessage(response, 'data.message')));
		}
		
	return response;
};
export const postDownloadForIBStudyDocument = (url, data, { type, ...options }) => async (dispatch) => {
	let response = await dispatch(
		sendRequest({
			...options,
			method: 'post',
			responseType: data.action ===  'VIEW_IB_STUDY_DOC' ? 'application/json' : 'arraybuffer',
			headers: { 'Content-Type': 'application/json' },
			url,
			data
		})
	);
	// View Ib Study doc - Added condition to return response directly without saving the file
	
	if(data && data.action === 'VIEW_IB_STUDY_DOC'){
		return response;
	}
	
	let fileName = 'StudyDocument.pdf'; // Default filename

	// Extracting filename from the request data
	if (data && data.fileName) {
		fileName = data.fileName.includes('_') ? data.fileName.substring(data.fileName.indexOf('_') + 1) : data.fileName;
	}
	
	if(response && response.data && response.data.byteArray){
		FileSaver.saveAs(new Blob([getArrayBuffer(response.data.byteArray)], { type }), fileName);
	} else if(response && response.data){
		FileSaver.saveAs(new Blob([response.data], { type }), fileName);
	} 
		
	dispatch(successMessage(getMessage(response, 'data.message')));
	return response;



	
};
// SAF-18432-Safety - User Guide Integration- To View User Guide
export const getUserGuideForView = (url) => async (dispatch) => {
	const response = await dispatch(
		sendRequest({
			method: 'get',
			responseType: 'arraybuffer',
			url: url
		})
	);
	return response;
};
// SAF-18432-Safety - User Guide Integration- To Download User Guide
export const getUserGuideForDownload = (url, fileName) => async (dispatch) => {
	const response = await dispatch(
		sendRequest({
			method: 'get',
			responseType: 'arraybuffer',
			url: url
		})
	);
	if (response && response.data) {
		FileSaver.saveAs(new Blob([response.data], { type: 'application/pdf' }), fileName || 'user-guide.pdf');
	}
	return response;
};